/**
 * Единый визуальный язык состояний Оборудования. Связь и Готовность — разные
 * утверждения, поэтому у них разные палитры и они нигде не сливаются в один цвет.
 */

export const DEVICE_TYPES = ['door', 'panic', 'nvr', 'fire'];

export const TYPE_META = {
  door: { icon: 'sensor_door', route: 'doors' },
  panic: { icon: 'emergency', route: 'panic' },
  nvr: { icon: 'videocam', route: 'nvr' },
  fire: { icon: 'local_fire_department', route: 'nvr' },
};

export const deviceHref = (device, routerIp) => {
  if (!routerIp || !TYPE_META[device.deviceType]) return null;
  const id = device.linkId || device.id;
  return `/schools/${encodeURIComponent(routerIp)}/${TYPE_META[device.deviceType].route}/${id}`;
};

/**
 * Одна палитра на весь продукт: positive / warning / critical / muted.
 * На экране состояние читается точкой, как на главной и у регистратора.
 */
export const CONNECTIVITY_META = {
  available: {
    tone: 'text-positive',
    dot: 'bg-positive',
    pill: 'border-positive/40 bg-positive/10 text-positive',
    icon: 'wifi_tethering',
  },
  unavailable: {
    tone: 'text-critical',
    dot: 'bg-critical',
    pill: 'border-critical/40 bg-critical/10 text-critical',
    icon: 'power_settings_new',
  },
  polling_disabled: {
    tone: 'text-muted-500',
    dot: 'bg-muted-600',
    pill: 'border-surface-border bg-surface-subtle/60 text-muted-400',
    icon: 'do_not_disturb',
  },
  no_data: {
    tone: 'text-muted-400',
    dot: 'bg-muted-500',
    pill: 'border-surface-border bg-surface-subtle/60 text-muted-400',
    icon: 'help_outline',
  },
  not_configured: {
    tone: 'text-muted-400',
    dot: 'bg-muted-500',
    pill: 'border-surface-border bg-surface-subtle/60 text-muted-400',
    icon: 'settings',
  },
};

export const READINESS_META = {
  ready: {
    tone: 'text-positive',
    dot: 'bg-positive',
    pill: 'border-positive/40 bg-positive/10 text-positive',
    icon: 'verified',
  },
  not_ready: {
    tone: 'text-warning',
    dot: 'bg-warning',
    pill: 'border-warning/40 bg-warning/10 text-warning',
    icon: 'error_outline',
  },
  unknown: {
    tone: 'text-muted-400',
    dot: 'bg-muted-500',
    pill: 'border-surface-border bg-surface-subtle/60 text-muted-400',
    icon: 'help_outline',
  },
};

/** Тон точки статуса, как на главной и на странице регистратора. */
export const statusDotTone = (state) => {
  if (state === 'available' || state === 'ready') return 'ok';
  if (state === 'unavailable') return 'critical';
  if (state === 'not_ready') return 'warning';
  return 'muted';
};

export const SEVERITY = {
  OK: 'ok',
  WARNING: 'warning',
  CRITICAL: 'critical',
  MUTED: 'muted',
};

/**
 * Цветом говорит состояние, а видом Оборудования — глиф. Поэтому у карточки
 * ровно один источник цвета, и «турникет» не спорит за палитру с «недоступен».
 */
export const deviceSeverity = (device, { muted = false } = {}) => {
  if (muted) return SEVERITY.MUTED;
  if (device?.connectivity === 'unavailable') return SEVERITY.CRITICAL;
  if (device?.readiness?.state === 'not_ready') return SEVERITY.WARNING;
  if (device?.connectivity === 'available') return SEVERITY.OK;
  return SEVERITY.MUTED;
};

export const SEVERITY_META = {
  [SEVERITY.OK]: {
    text: 'text-positive',
    tile: 'bg-positive/15 text-positive',
    accent: 'bg-positive/70',
    shell: 'border-surface-border bg-surface-subtle/50',
    hover: 'hover:border-positive/40',
  },
  [SEVERITY.WARNING]: {
    text: 'text-warning',
    tile: 'bg-warning/15 text-warning',
    accent: 'bg-warning/80',
    shell: 'border-warning/40 bg-warning/10',
    hover: 'hover:border-warning/50',
  },
  [SEVERITY.CRITICAL]: {
    text: 'text-critical',
    tile: 'bg-critical/15 text-critical',
    accent: 'bg-critical/80',
    shell: 'border-critical/40 bg-critical/10',
    hover: 'hover:border-critical/50',
  },
  [SEVERITY.MUTED]: {
    text: 'text-muted-400',
    tile: 'bg-muted-600/25 text-muted-400',
    accent: 'bg-muted-600/70',
    shell: 'border-surface-border bg-surface-subtle/50',
    hover: 'hover:border-brand-400/40',
  },
};

/** Сводка по виду Оборудования внутри школы: что показать и каким тоном. */
export const summarizeType = (summary) => {
  const total = summary?.total || 0;
  const unavailable = summary?.connectivity?.unavailable || 0;
  const notReady = summary?.readiness?.not_ready || 0;
  const notConfigured = summary?.connectivity?.not_configured || 0;
  const ready = summary?.readiness?.ready || 0;

  return {
    total,
    ready,
    unavailable,
    notReady,
    notConfigured,
    // Для пожарки «проблема» = не готова, не настроена или без связи.
    problem: notReady + notConfigured + unavailable,
    hasProblem: unavailable > 0 || notReady > 0,
  };
};

export const schoolHasProblem = (school) => (
  school.channel === 'unavailable'
  || DEVICE_TYPES.some((type) => {
    const s = summarizeType(school.byType?.[type]);
    if (type === 'fire') return s.problem > 0;
    return s.hasProblem;
  })
);
